import { useCallback, useEffect, useMemo, useState } from "react";
import { Save, Pencil, Target as TargetIcon } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, money } from "@/lib/api";
import { PageHead, Kpi, EmptyState } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const monthOptions = () => {
  const out = [];
  const now = new Date();
  for (let i = -6; i <= 6; i += 1) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  }
  return out;
};

export default function Targets() {
  const months = useMemo(monthOptions, []);
  const [studios, setStudios] = useState([]);
  const [services, setServices] = useState([]);
  const [studioId, setStudioId] = useState("");
  const [month, setMonth] = useState(months[6]);
  const [progress, setProgress] = useState(null);
  const [revenue, setRevenue] = useState("");
  const [units, setUnits] = useState({});
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [hasTarget, setHasTarget] = useState(false);

  useEffect(() => {
    api.get("/studios").then(({ data }) => {
      setStudios(data);
      setStudioId((s) => s || (data[0]?.id ?? ""));
    });
    api.get("/config/services").then(({ data }) => setServices(data));
  }, []);

  const load = useCallback(async () => {
    if (!studioId) return;
    const [{ data: prog }, { data: list }] = await Promise.all([
      api.get("/targets/progress", { params: { studio_id: studioId, month } }),
      api.get("/targets", { params: { month } }),
    ]);
    const block = prog.studios?.[0] || null;
    setProgress(block);
    const saved = (list || []).find((t) => t.studio_id === studioId);
    setRevenue(String(saved?.revenue_target || ""));
    const draft = {};
    (block?.rows || []).forEach((r) => {
      if (r.target) draft[r.service] = String(r.target);
    });
    setUnits(draft);
    setHasTarget(Number(saved?.revenue_target || 0) > 0 || Object.keys(draft).length > 0);
    setEditing(false);
  }, [studioId, month]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      const service_targets = {};
      Object.entries(units).forEach(([k, v]) => {
        if (Number(v) > 0) service_targets[k] = Number(v);
      });
      await api.put("/targets", {
        studio_id: studioId,
        month,
        revenue_target: Number(revenue || 0),
        service_targets,
      });
      toast.success("Targets saved");
      await load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const rowsByService = Object.fromEntries((progress?.rows || []).map((r) => [r.service, r]));
  const targetedServices = services.filter((s) => (units[s.name] ?? "") !== "" || rowsByService[s.name]?.target);
  const showForm = editing || !hasTarget;

  return (
    <div>
      <PageHead title="Monthly targets" subtitle="Revenue and per-service unit targets by studio">
        {studios.length > 0 && hasTarget && !editing && (
          <Button
            className="min-h-[44px]"
            variant="outline"
            data-testid="targets-edit-btn"
            onClick={() => setEditing(true)}
          >
            <Pencil className="mr-2 h-4 w-4" /> Edit targets
          </Button>
        )}
      </PageHead>

      {studios.length === 0 ? (
        <EmptyState
          icon={TargetIcon}
          testid="targets-empty-state"
          title="No studios yet"
          hint="Add a studio first — targets are set per studio, per month, per service type."
        />
      ) : (
        <>
          <Card className="mb-6 border-brand-line shadow-none">
            <CardContent className="flex flex-wrap items-end gap-4 p-6">
              <div>
                <Label>Studio</Label>
                <Select value={studioId} onValueChange={setStudioId}>
                  <SelectTrigger className="mt-1.5 min-h-[44px] w-64" data-testid="targets-studio-select">
                    <SelectValue placeholder="Select studio" />
                  </SelectTrigger>
                  <SelectContent>
                    {studios.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name} · {s.city}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Month</Label>
                <Select value={month} onValueChange={setMonth}>
                  <SelectTrigger className="mt-1.5 min-h-[44px] w-40" data-testid="targets-month-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map((m) => (
                      <SelectItem key={m} value={m}>
                        {m}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <div className="mb-6 grid gap-6 sm:grid-cols-3">
            <Kpi label="Overall achievement" value={`${progress?.overall_pct ?? 0}%`} testid="targets-overall-pct" />
            <Kpi
              label="Units targeted"
              value={progress?.total_target ?? 0}
              hint={`${progress?.total_actual ?? 0} delivered this month`}
              testid="targets-total-units"
            />
            <Kpi label="Revenue target" value={money(Number(revenue || 0))} testid="targets-revenue-kpi" />
          </div>

          {showForm ? (
            /* ---------- editable form ---------- */
            <Card className="border-brand-line shadow-none" data-testid="targets-form">
              <CardHeader className="flex-row flex-wrap items-center justify-between gap-2 space-y-0">
                <div>
                  <CardTitle className="text-lg">
                    {hasTarget ? "Edit targets" : `Set targets for ${month}`}
                  </CardTitle>
                  <p className="mt-1 text-xs text-slate-500">
                    Enter the revenue goal and how many jobs of each service type this studio should deliver.
                  </p>
                </div>
                <div className="flex gap-2">
                  {hasTarget && (
                    <Button variant="ghost" className="min-h-[44px]" data-testid="targets-cancel-btn" onClick={() => { setEditing(false); load(); }}>
                      Cancel
                    </Button>
                  )}
                  <Button className="min-h-[44px]" data-testid="targets-save-btn" disabled={busy || !studioId} onClick={save}>
                    <Save className="mr-2 h-4 w-4" /> Save targets
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="max-w-xs">
                  <Label>Revenue target (₹)</Label>
                  <Input
                    className="mt-1.5 min-h-[44px]"
                    inputMode="decimal"
                    data-testid="targets-revenue-input"
                    value={revenue}
                    onChange={(e) => setRevenue(e.target.value)}
                  />
                </div>
                <div className="overflow-x-auto">
                  <Table data-testid="targets-table" className="mobile-cards">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Service type</TableHead>
                        <TableHead className="text-right">Target units</TableHead>
                        <TableHead className="text-right">Delivered</TableHead>
                        <TableHead className="text-right">Achieved</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {services.map((s) => {
                        const row = rowsByService[s.name];
                        return (
                          <TableRow key={s.id} data-testid={`targets-row-${s.name}`}>
                            <TableCell data-label="Service type" className="font-medium">{s.name}</TableCell>
                            <TableCell data-label="Target units" className="text-right">
                              <Input
                                className="ml-auto min-h-[44px] w-24 text-right"
                                inputMode="numeric"
                                data-testid={`targets-input-${s.name}`}
                                value={units[s.name] ?? ""}
                                onChange={(e) => setUnits((u) => ({ ...u, [s.name]: e.target.value }))}
                              />
                            </TableCell>
                            <TableCell data-label="Delivered" className="text-right font-semibold">{row?.actual ?? 0}</TableCell>
                            <TableCell data-label="Achieved" className="text-right">
                              {row?.pct == null ? (
                                <span className="text-xs text-slate-400">no target</span>
                              ) : (
                                <span className={`font-semibold ${row.pct >= 70 ? "text-emerald-600" : "text-crimson-600"}`}>
                                  {row.pct}%
                                </span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          ) : (
            /* ---------- read-only summary ---------- */
            <Card className="border-brand-line shadow-none" data-testid="targets-readonly">
              <CardHeader>
                <CardTitle className="text-lg">Targets set for {month}</CardTitle>
                <p className="mt-1 text-xs text-slate-500">
                  Revenue goal <strong className="text-brand-ink">{money(Number(revenue || 0))}</strong>
                </p>
              </CardHeader>
              <CardContent className="overflow-x-auto p-0">
                {targetedServices.length === 0 ? (
                  <p className="p-6 text-sm text-slate-500">
                    A revenue target is set but no per-service unit targets. Tap Edit to add them.
                  </p>
                ) : (
                  <Table data-testid="targets-readonly-table" className="mobile-cards">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Service type</TableHead>
                        <TableHead className="text-right">Target units</TableHead>
                        <TableHead className="text-right">Delivered</TableHead>
                        <TableHead className="text-right">Achieved</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {targetedServices.map((s) => {
                        const row = rowsByService[s.name];
                        return (
                          <TableRow key={s.id} data-testid={`targets-readonly-row-${s.name}`}>
                            <TableCell data-label="Service type" className="font-medium">{s.name}</TableCell>
                            <TableCell data-label="Target units" className="text-right font-semibold">
                              {units[s.name] ?? row?.target ?? "—"}
                            </TableCell>
                            <TableCell data-label="Delivered" className="text-right">{row?.actual ?? 0}</TableCell>
                            <TableCell data-label="Achieved" className="text-right">
                              {row?.pct == null ? (
                                <span className="text-xs text-slate-400">—</span>
                              ) : (
                                <span className={`font-semibold ${row.pct >= 70 ? "text-emerald-600" : "text-crimson-600"}`}>
                                  {row.pct}%
                                </span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
